import type { JSX, ComponentChildren } from 'preact';

type Variant = 'ember' | 'primary' | 'secondary' | 'ghost';
type Size = 'lg' | 'md' | 'sm';

type Props = {
  variant?: Variant;
  size?: Size;
  full?: boolean;
  href?: string;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  children: ComponentChildren;
} & Omit<JSX.HTMLAttributes<HTMLButtonElement>, 'size' | 'type' | 'disabled'>;

const PAD: Record<Size, string> = {
  lg: '16px 22px',
  md: '13px 18px',
  sm: '8px 14px',
};
const RADIUS: Record<Size, string> = { lg: '16px', md: '14px', sm: '10px' };
const FONT: Record<Size, string> = { lg: '17px', md: '15px', sm: '13px' };

const VARIANT_STYLE: Record<Variant, JSX.CSSProperties> = {
  ember: {
    background: 'var(--ember)',
    color: '#fff',
    boxShadow:
      '0 1px 0 rgba(255,255,255,0.18) inset, 0 4px 14px rgba(233,74,31,0.30)',
  },
  primary: {
    background: 'var(--ink)',
    color: 'var(--surface)',
    boxShadow:
      '0 1px 0 rgba(255,255,255,0.06) inset, 0 2px 8px rgba(27,22,17,0.15)',
  },
  secondary: {
    background: 'var(--card)',
    color: 'var(--ink)',
    boxShadow: '0 0 0 1px var(--line)',
  },
  ghost: {
    background: 'transparent',
    color: 'var(--ink)',
  },
};

export function Button({
  variant = 'ember',
  size = 'lg',
  full,
  href,
  type = 'button',
  children,
  style,
  ...rest
}: Props) {
  const base: JSX.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: PAD[size],
    borderRadius: RADIUS[size],
    fontSize: FONT[size],
    fontWeight: 600,
    letterSpacing: '-0.1px',
    border: 'none',
    cursor: 'pointer',
    width: full ? '100%' : undefined,
    fontFamily: 'var(--font-ui)',
    transition: 'transform 120ms ease, opacity 120ms ease',
    ...VARIANT_STYLE[variant],
    ...(style as JSX.CSSProperties),
  };

  if (href) {
    return (
      <a href={href} style={{ ...base, textDecoration: 'none' }}>
        {children}
      </a>
    );
  }
  return (
    <button type={type} style={base} {...rest}>
      {children}
    </button>
  );
}
